// TransportOrderJsonViewer.tsx
import * as React from "react";
import {useEffect, useState} from "react";
import {Button, Checkbox, Form, Header, Icon, Segment} from "semantic-ui-react";
import {authentication} from "../../../../../authentication";
import {backend} from "../../../../../xconvert-backend";
import AceEditor from "react-ace";
import {notify} from 'react-notify-toast';
import {TransportOrder} from "../../../../../generated/api";
import {FaFile} from "react-icons/fa";
import {FilesViewerModal} from "../../../../util/fileViewer/FilesViewerModal";

export interface TransportOrderJsonViewerProps {
    transportOrder: string | null;
    readOnly?: boolean;
    onChange?: (newJson: string) => void;
}

export interface OptimizeJsonSettings {
    removeNulls?: boolean;
    removeContractingParties?: boolean;
    removeActions?: boolean;
    removeStatus?: boolean;
    removeAttachments?: boolean;
    removeStatusValidationResults?: boolean;
    removeEmptyAdditionalDataLists?: boolean;
    takeFirstNTransportsAndRelatedFreight?: number | null;
}

async function optimizeTransportOrderJsonApi(
    transportOrderJson: string,
    settings: OptimizeJsonSettings
): Promise<string> {
    const payload = {
        transportOrderJson: transportOrderJson,
        settings: settings,
    };
    const uri = "/converter/v1/optimizeTransportOrderJson";

    const requestHeaders: Record<string, string> = {
        "Content-Type": "application/json; charset=utf-8",
    };
    const authHeader = backend.withTokenAuthHeader(authentication.token).headers;
    Object.assign(requestHeaders, authHeader);

    const response = await fetch(uri, {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorBody = await response.text();
        const displayError = errorBody || `Server error (${response.status})`;
        console.error(`Error optimizing transport order JSON (API: ${uri}):`, response.status, errorBody);
        throw new Error(displayError);
    }
    return response.text();
}

export const initialSettings: OptimizeJsonSettings = {
    removeNulls: false,
    removeContractingParties: false,
    removeActions: false,
    removeStatus: false,
    removeAttachments: false,
    removeStatusValidationResults: false,
    removeEmptyAdditionalDataLists: false,
    takeFirstNTransportsAndRelatedFreight: null,
};

export function TransportOrderJsonViewer(props: TransportOrderJsonViewerProps) {

    const [transportOrderJson, setTransportOrderJson] = useState<string>(props.transportOrder ? props.transportOrder : "{}");
    const [isOptimized, setIsOptimized] = useState<boolean>(false);
    const [isOptimizing, setIsOptimizing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [currentOptimizeSettings, setCurrentOptimizeSettings] = useState<OptimizeJsonSettings | null>(null); // that is used
    const [uiOptimizeSettings, setUiOptimizeSettings] = useState<OptimizeJsonSettings>({...initialSettings}); // that is shown

    const [isOptimizationSettingsVisible, setIsOptimizationSettingsVisible] = useState<boolean>(false);

    const [showAttachmentsModal, setShowAttachmentsModal] = useState<boolean>(false);
    const [parsedOrder, setParsedOrder] = useState<TransportOrder | null>(null);

    const editorRef = React.useRef<any>(null);

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.resize();
        }
    }, [isOptimizationSettingsVisible, transportOrderJson]);

    useEffect(() => {
        setIsOptimized(false)
        setCurrentOptimizeSettings(initialSettings);
        setTransportOrderJson(props.transportOrder ?? "");
        //handleApplyOptimization().then(() => {})
    }, [props.transportOrder]);


    function handleSettingChange(settingName: keyof OptimizeJsonSettings, value: boolean | number | null) {
        const newUiSettings = {...uiOptimizeSettings, [settingName]: value};
        setUiOptimizeSettings(newUiSettings);
        setIsOptimized(true);
    }

    async function handleApplyOptimization() {
        if (!props.transportOrder) return;

        setIsOptimizing(true);
        try {
            const jsonToOptimize = props.transportOrder ?? "{}";
            const optimizedJsonString = await optimizeTransportOrderJsonApi(jsonToOptimize, uiOptimizeSettings);
            setCurrentOptimizeSettings(uiOptimizeSettings);
            setTransportOrderJson(optimizedJsonString);
            setIsOptimized(true);

            notify.show('JSON Optimized!', 'success', 2000);
        } catch (err: any) {
            notify.show(`Optimization failed: ${err.message}`, 'error', 3000);
        } finally {
            setIsOptimizing(false);
        }
    }

    function handleRevertToOriginal() {
        if (!props.transportOrder) return;

        setIsOptimized(false);
        setTransportOrderJson(props.transportOrder);
        setCurrentOptimizeSettings(null);
        setUiOptimizeSettings({...initialSettings});
        notify.show('Reverted to original.', 'info', 2000);

    }

    function tryParseJson(jsonString: string): TransportOrder | null {
        if (!jsonString) return null;
        try {
            let order = JSON.parse(jsonString);
            setError(null);
            return order;
        } catch (e) {
            console.error("Error parsing JSON:", e);
            setError("Invalid JSON format.");
            return null;
        }
    }

    function gotAttachments() {
        if (!props.transportOrder) return false;
        if (!parsedOrder) return false;

        let totalAttachments = parsedOrder.attachments?.length || 0;
        parsedOrder.transports?.forEach((t: any) => {
            totalAttachments += t.attachments?.length || 0;
            t.stops?.forEach((s: any) => {
                totalAttachments += s.attachments?.length || 0;
            })
        });
        parsedOrder.freight?.forEach((f: any) => {
            totalAttachments += f.attachments?.length || 0;
        });
        return totalAttachments > 0;
    }

    function renderOptimizationControls(order: any) {
        if (!order || error) return null;
        const s = uiOptimizeSettings;

        const checkboxGroupStyle = {
            display: 'flex',
            flexWrap: 'wrap' as 'wrap',
            alignItems: 'center'
        };
        const checkboxFieldStyle = {marginRight: '1.5em', marginBottom: '0.6em'}; // Applied to each Form.Field

        const totalNrOfAttachments = () => {
            if (!props.transportOrder) return 0;
            if (!parsedOrder) return 0;

            let total = parsedOrder?.attachments?.length || 0;
            parsedOrder.transports?.forEach((t: any) => {
                total += t.attachments?.length || 0;
                t.stops?.forEach((s: any) => {
                    total += s.attachments?.length || 0;
                })
            })
            parsedOrder.freight?.forEach((f: any) => {
                total += f.attachments?.length || 0;
            })
            return total;
        }

        return (
            <Segment raised style={{padding: '0.5em', marginBottom: '0.5em', backgroundColor: '#fcfcfc'}}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom:'0'
                }}>
                    <Header as="h5" style={{
                        margin: 0,
                        fontSize: '1em',
                        display: 'flex',
                        alignItems: 'center',
                        flexGrow: 1,
                        overflow: 'hidden'
                    }}>
                        <Icon name="cogs" style={{marginRight: '0.4em', opacity: 0.8}}/>
                        <span style={{
                            flexShrink: 0,
                            marginRight: '10px',
                            whiteSpace: 'nowrap'
                        }}>Optimization Settings</span>
                        {order.isOptimized && <span style={{
                            color: 'green',
                            fontSize: '0.9em',
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            marginLeft: 'auto'
                        }}>(Optimized)</span>}
                    </Header>
                    <Button
                        icon basic size="tiny"
                        onClick={() => setIsOptimizationSettingsVisible(!isOptimizationSettingsVisible)}
                        aria-label={isOptimizationSettingsVisible ? "Hide settings" : "Show settings"}
                        style={{padding: '0.5em', marginLeft: '0.5em', flexShrink: 0}}
                    >
                        <Icon name={isOptimizationSettingsVisible ? "chevron up" : "chevron down"}/>
                    </Button>
                </div>

                {isOptimizationSettingsVisible && (
                    <>
                        <div style={{
                            maxHeight: '160px',
                            overflowY: 'auto',
                            padding: '0.2em 0.5em 0.2em 0.2em',
                            //marginBottom: '0.75em',
                            borderTop: '1px solid #eee',
                            paddingTop: '0.5em'
                        }}>
                            <Form size="tiny">
                                <div style={checkboxGroupStyle}>
                                    <Form.Field style={checkboxFieldStyle}>
                                        <Checkbox label="No Nulls"
                                                  checked={!!s.removeNulls}
                                                  onChange={(_, d) => handleSettingChange('removeNulls', d.checked || false)}/>
                                    </Form.Field>
                                    <Form.Field style={checkboxFieldStyle}> <Checkbox label="No ContractingParties"
                                                                                      checked={!!s.removeContractingParties}
                                                                                      onChange={(_, d) => handleSettingChange('removeContractingParties', d.checked || false)}/>
                                    </Form.Field>
                                    <Form.Field style={checkboxFieldStyle}> <Checkbox label="No Actions"
                                                                                      checked={!!s.removeActions}
                                                                                      onChange={(_, d) => handleSettingChange('removeActions', d.checked || false)}/>
                                    </Form.Field>
                                    <Form.Field style={checkboxFieldStyle}> <Checkbox label="No Status"
                                                                                      checked={!!s.removeStatus}
                                                                                      onChange={(_, d) => handleSettingChange('removeStatus', d.checked || false)}/>
                                    </Form.Field>
                                    <Form.Field style={checkboxFieldStyle}> <Checkbox label="No Attachments"
                                                                                      checked={!!s.removeAttachments}
                                                                                      onChange={(_, d) => handleSettingChange('removeAttachments', d.checked || false)}/>
                                    </Form.Field>
                                    <Form.Field style={checkboxFieldStyle}> <Checkbox label="No statusValidationResult"
                                                                                      checked={!!s.removeStatusValidationResults}
                                                                                      onChange={(_, d) => handleSettingChange('removeStatusValidationResults', d.checked || false)}/>
                                    </Form.Field>
                                    <Form.Field style={checkboxFieldStyle}> <Checkbox label="No Empty Add.Data"
                                                                                      checked={!!s.removeEmptyAdditionalDataLists}
                                                                                      onChange={(_, d) => handleSettingChange('removeEmptyAdditionalDataLists', d.checked || false)}/>
                                    </Form.Field>

                                    <Form.Field style={{...checkboxFieldStyle, display: 'flex', alignItems: 'center'}}>
                                        <label
                                            htmlFor={`n-transports-${order.id}`}
                                            style={{
                                                marginRight: '0.5em',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                                fontWeight: 'normal', // Match Checkbox label weight
                                            }}
                                        >
                                            NTransportsNFreights:
                                        </label>
                                        <Form.Input
                                            id={`n-transports-${order.id}`}
                                            type="number"
                                            placeholder="All"
                                            size="mini" // Make the input field smaller
                                            style={{width: '75px'}} // Adjusted width
                                            value={s.takeFirstNTransportsAndRelatedFreight === null || s.takeFirstNTransportsAndRelatedFreight === undefined ? '' : s.takeFirstNTransportsAndRelatedFreight}
                                            onChange={(_, d) => handleSettingChange('takeFirstNTransportsAndRelatedFreight', d.value === '' ? null : parseInt(d.value))}
                                        />
                                    </Form.Field>
                                </div>
                            </Form>
                        </div>
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <div> {gotAttachments() && <Button
                                onClick={() => {
                                    setShowAttachmentsModal(true)
                                }}
                            >
                                <FaFile/> display {totalNrOfAttachments()} attachments
                            </Button>
                            }
                            </div>
                            <div style={{textAlign: 'right'}}>
                                <Button
                                    primary icon labelPosition="left"
                                    onClick={handleApplyOptimization}
                                    loading={isOptimizing}
                                    disabled={isOptimizing || uiOptimizeSettings == currentOptimizeSettings}
                                    size="small"
                                > <Icon name="magic"/> Apply </Button>
                                <Button
                                    icon labelPosition="left"
                                    onClick={handleRevertToOriginal}
                                    disabled={!isOptimized}
                                    style={{marginLeft: '0.75em'}}
                                    size="small"
                                > <Icon name="undo alternate"/> Revert </Button>
                            </div>
                        </div>
                    </>
                )}
            </Segment>
        );
    };

    function combineChangesWithOriginal(originalJson: string, newJson: string): string {
        try {
            const originalObj = JSON.parse(originalJson);
            const newObj = JSON.parse(newJson);

            // Combine the two objects, giving precedence to the new object
            const combinedObj = {...originalObj, ...newObj};

            return JSON.stringify(combinedObj, null, 2);
        } catch (e) {
            console.error("Error combining changes with original JSON:", e);
            return newJson; // Fallback to newJson if parsing fails
        }
    }

    return (
        <Segment basic style={{
            margin: '0',
            padding: '0.1em',
            backgroundColor: '#f7f7f7',
            display: 'flex',
            flexDirection: 'column',
            flex: "available",
            flexGrow: 1,
        }}>
            <div style={{
                flex: "available",
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                //marginBottom: '0.75em',
                paddingBottom: '0.5em',
                borderBottom: '1px solid #ddd'
            }}>
                {error && (
                    <Segment
                        color="red"
                        tertiary
                    >
                        <Header
                            as="h4"
                            style={{color: '#9f3a38'}}
                        >
                            Error: {parsedOrder?._id ?? "Unknown ID"}
                        </Header>
                        <p>{error}</p>
                    </Segment>
                )}
                <>
                    {renderOptimizationControls(props.transportOrder)}
                    <AceEditor
                        mode="json"
                        theme="monokai"
                        name={`json-${parsedOrder?._id ?? "Unknown ID"}`}
                        value={transportOrderJson}
                        readOnly={props.readOnly ?? true}
                        width="100%"
                        fontSize={12.5}
                        style={{
                            flex: "1",
                            display: "flex",
                        }}
                        setOptions={{
                            useWorker: false,
                            showPrintMargin: false,
                            highlightActiveLine: true,
                            wrap: true,
                            fadeFoldWidgets: true
                        }}
                        //editorProps={{$blockScrolling: true}}
                        onChange={(newValue) => {
                            setTransportOrderJson(newValue);
                            props.onChange(combineChangesWithOriginal(props.transportOrder, newValue));
                        }}
                        onLoad={(editor) => {
                            // Editor-Instanz speichern, z.B. in einem Ref
                            editorRef.current = editor;
                        }}
                    />
                </>

            </div>
            {showAttachmentsModal &&
                <FilesViewerModal
                    isOpen={showAttachmentsModal}
                    onClose={() => setShowAttachmentsModal(null)}
                    order={parsedOrder}
                />
            }
        </Segment>
    );
}